import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import { allWhereDoYouFeelMostAtHome } from '$/_const/dna/WhereDoYouFeelMostAtHome.ts';
import type { WhereDoYouFeelMostAtHome } from '$/_const/dna/WhereDoYouFeelMostAtHome.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaImageChoiceCard from '@/components/functional/TravelDnaManipulationForm/shared/DnaImageChoiceCard.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

import outInNatureImage from '@/assets/img/onboarding/sanctuary/out_in_nature.jpg';
import byTheSeaImage from '@/assets/img/onboarding/sanctuary/by_the_sea.jpg';
import buzzingCityImage from '@/assets/img/onboarding/sanctuary/buzzing_city.jpg';
import quietTownsAndVillagesImage from '@/assets/img/onboarding/sanctuary/quiet_towns_and_villages.jpg';

const yourSanctuarySchema = dnaSchema.pick({
  where_do_you_feel_most_at_home: true,
});

export type YourSanctuarySchema = z.infer<typeof yourSanctuarySchema>;

type YourSanctuaryProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const sanctuaryImages: Record<WhereDoYouFeelMostAtHome, string> = {
  out_in_nature: outInNatureImage,
  by_the_sea: byTheSeaImage,
  buzzing_city: buzzingCityImage,
  quiet_towns_and_villages: quietTownsAndVillagesImage,
};

function YourSanctuary({
  onComplete,
  onCompleteWhenDirty,
}: YourSanctuaryProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<YourSanctuarySchema>({
    resolver: zodResolver(yourSanctuarySchema),
    defaultValues: {
      where_do_you_feel_most_at_home:
        contextData?.where_do_you_feel_most_at_home ?? [],
    },
  });

  const onSubmit = (data: YourSanctuarySchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_SANCTUARY,
          data,
          context,
        };

        if (isDirty) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourSanctuary.header')}
      title={t('travelDnaPage.yourSanctuary.subheader')}
      description={t('travelDnaPage.yourSanctuary.description')}
    >
      <form
        className="mt-6 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-6" />

        <Controller
          control={control}
          name="where_do_you_feel_most_at_home"
          render={({ field: { onChange, value }, fieldState }) => {
            const selectedOptions = value ?? [];
            const toggle = (option: WhereDoYouFeelMostAtHome) => {
              if (selectedOptions.includes(option)) {
                onChange(selectedOptions.filter((item) => item !== option));
              } else if (selectedOptions.length < 2) {
                onChange([...selectedOptions, option]);
              }
            };

            return (
              <>
                <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-7">
                  {allWhereDoYouFeelMostAtHome.map((option) => {
                    const selected = selectedOptions.includes(option);
                    const atLimit = selectedOptions.length === 2;
                    return (
                      <DnaImageChoiceCard
                        key={option}
                        image={sanctuaryImages[option]}
                        label={t(
                          `travelDnaPage.yourSanctuary.options.${option}`,
                          missingTranslationToReadable(option)
                        )}
                        selected={selected}
                        dimmed={selectedOptions.length > 0 && !selected}
                        disabled={isPending || (atLimit && !selected)}
                        onClick={() => toggle(option)}
                      />
                    );
                  })}
                </div>

                {fieldState.error ? (
                  <p className="mt-3 w-full text-left font-sans text-sm text-error" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </>
            );
          }}
        />

        <DnaPrimaryButton className="mt-8" loading={isPending} type="submit">
          {t('travelDnaPage.yourSanctuary.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourSanctuary;
